import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Filter, X, RotateCcw } from 'lucide-react';

export interface FilterValues {
  status: string;
  provider: string;
  project: string;
  brevoSent: boolean;
  brevoDelivered: boolean;
  brevoOpened: boolean;
  brevoHardBounced: boolean;
  brevoSoftBounced: boolean;
  brevoUnsubscribed: boolean;
  brevoComplained: boolean;
  brevoBlocked: boolean;
  brevoDeferred: boolean;
  domainDisposable: boolean;
  domainFree: boolean;
  accountFullMailbox: boolean;
  blocklist: string;
}

const defaultFilterValues: FilterValues = {
  status: 'all',
  provider: 'all',
  project: 'all',
  brevoSent: false,
  brevoDelivered: false,
  brevoOpened: false,
  brevoHardBounced: false,
  brevoSoftBounced: false,
  brevoUnsubscribed: false,
  brevoComplained: false,
  brevoBlocked: false,
  brevoDeferred: false,
  domainDisposable: false,
  domainFree: false,
  accountFullMailbox: false,
  blocklist: 'all',
};

interface EmailFiltersProps {
  filters: FilterValues;
  onApply: (filters: FilterValues) => void;
  onReset: () => void;
  providers: string[];
}

export { defaultFilterValues };

export default function EmailFilters({ filters, onApply, onReset, providers }: EmailFiltersProps) {
  const [open, setOpen] = useState(false);
  const [pending, setPending] = useState<FilterValues>({ ...filters });

  const handleOpen = (isOpen: boolean) => {
    if (isOpen) setPending({ ...filters });
    setOpen(isOpen);
  };

  const handleApply = () => {
    onApply(pending);
    setOpen(false);
  };

  const handleReset = () => {
    onReset();
    setPending({ ...defaultFilterValues });
    setOpen(false);
  };

  // Active filter pills
  const activeFilters: { label: string; key: keyof FilterValues; value: string }[] = [];
  if (filters.status !== 'all') activeFilters.push({ label: `Status: ${filters.status}`, key: 'status', value: 'all' });
  if (filters.provider !== 'all') activeFilters.push({ label: `Provider: ${filters.provider}`, key: 'provider', value: 'all' });
  if (filters.brevoSent) activeFilters.push({ label: 'Delivery: Sent', key: 'brevoSent', value: 'false' });
  if (filters.brevoDelivered) activeFilters.push({ label: 'Delivery: Delivered', key: 'brevoDelivered', value: 'false' });
  if (filters.brevoOpened) activeFilters.push({ label: 'Delivery: Opened', key: 'brevoOpened', value: 'false' });
  if (filters.brevoHardBounced) activeFilters.push({ label: 'Delivery: Hard Bounce', key: 'brevoHardBounced', value: 'false' });
  if (filters.brevoSoftBounced) activeFilters.push({ label: 'Delivery: Soft Bounce', key: 'brevoSoftBounced', value: 'false' });
  if (filters.brevoUnsubscribed) activeFilters.push({ label: 'Delivery: Unsubscribed', key: 'brevoUnsubscribed', value: 'false' });
  if (filters.brevoComplained) activeFilters.push({ label: 'Delivery: Complained', key: 'brevoComplained', value: 'false' });
  if (filters.brevoBlocked) activeFilters.push({ label: 'Delivery: Blocked', key: 'brevoBlocked', value: 'false' });
  if (filters.brevoDeferred) activeFilters.push({ label: 'Delivery: Deferred', key: 'brevoDeferred', value: 'false' });
  if (filters.blocklist !== 'all') {
    const blocklistLabels: Record<string, string> = { any: 'Any', marketing: 'Marketing', transactional: 'Transactional', none: 'Not blocked' };
    activeFilters.push({ label: `Blocklist: ${blocklistLabels[filters.blocklist] || filters.blocklist}`, key: 'blocklist', value: 'all' });
  }
  if (filters.domainDisposable) activeFilters.push({ label: 'Disposable', key: 'domainDisposable', value: 'false' });
  if (filters.domainFree) activeFilters.push({ label: 'Free Provider', key: 'domainFree', value: 'false' });
  if (filters.accountFullMailbox) activeFilters.push({ label: 'Full Mailbox', key: 'accountFullMailbox', value: 'false' });

  const removeFilter = (key: keyof FilterValues, resetValue: string) => {
    const updated = { ...filters };
    if (resetValue === 'all') {
      (updated as any)[key] = 'all';
    } else {
      (updated as any)[key] = false;
    }
    onApply(updated);
  };

  return (
    <div className="flex flex-wrap items-center gap-2">
      <Popover open={open} onOpenChange={handleOpen}>
        <PopoverTrigger asChild>
          <Button variant="outline" size="sm">
            <Filter className="h-4 w-4 mr-2" />
            Filters
            {activeFilters.length > 0 && (
              <Badge variant="secondary" className="ml-2 h-5 w-5 rounded-full p-0 flex items-center justify-center text-[10px]">
                {activeFilters.length}
              </Badge>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-80" align="start">
          <div className="space-y-4">
            <div className="text-sm font-semibold">Filters</div>

            <div className="space-y-2">
              <label className="text-xs text-muted-foreground">Status</label>
              <Select
                value={pending.status}
                onValueChange={(v) => setPending({ ...pending, status: v })}
              >
                <SelectTrigger className="h-9 text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="deliverable">Deliverable</SelectItem>
                  <SelectItem value="risky">Risky</SelectItem>
                  <SelectItem value="undeliverable">Undeliverable</SelectItem>
                  <SelectItem value="unknown">Unknown</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <label className="text-xs text-muted-foreground">Provider</label>
              <Select
                value={pending.provider}
                onValueChange={(v) => setPending({ ...pending, provider: v })}
              >
                <SelectTrigger className="h-9 text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  {providers.map((p) => (
                    <SelectItem key={p} value={p}>{p}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <label className="text-xs text-muted-foreground">Delivery</label>
              <div className="flex flex-wrap gap-1.5">
                {([
                  { label: 'Sent', key: 'brevoSent' },
                  { label: 'Delivered', key: 'brevoDelivered' },
                  { label: 'Opened', key: 'brevoOpened' },
                  { label: 'Hard Bounce', key: 'brevoHardBounced' },
                  { label: 'Soft Bounce', key: 'brevoSoftBounced' },
                  { label: 'Unsubscribed', key: 'brevoUnsubscribed' },
                  { label: 'Complained', key: 'brevoComplained' },
                  { label: 'Blocked', key: 'brevoBlocked' },
                  { label: 'Deferred', key: 'brevoDeferred' },
                ] as const).map((item) => {
                  const active = pending[item.key as keyof FilterValues] as boolean;
                  return (
                    <button
                      key={item.key}
                      onClick={() => setPending({ ...pending, [item.key]: !active })}
                      className={`px-2.5 py-1 rounded-md border text-xs font-medium transition-colors ${
                        active
                          ? 'bg-primary text-primary-foreground border-primary'
                          : 'bg-background text-muted-foreground border-input hover:bg-muted'
                      }`}
                    >
                      {item.label}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-xs text-muted-foreground">Blocklist</label>
              <Select
                value={pending.blocklist}
                onValueChange={(v) => setPending({ ...pending, blocklist: v })}
              >
                <SelectTrigger className="h-9 text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="any">Blocklisted (any)</SelectItem>
                  <SelectItem value="marketing">Marketing blocked</SelectItem>
                  <SelectItem value="transactional">Transactional blocked</SelectItem>
                  <SelectItem value="none">Not blocked</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <label className="text-xs text-muted-foreground">Flags</label>
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="disposable"
                    checked={pending.domainDisposable}
                    onCheckedChange={(v) => setPending({ ...pending, domainDisposable: !!v })}
                  />
                  <label htmlFor="disposable" className="text-sm">Disposable</label>
                </div>
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="free"
                    checked={pending.domainFree}
                    onCheckedChange={(v) => setPending({ ...pending, domainFree: !!v })}
                  />
                  <label htmlFor="free" className="text-sm">Free Provider</label>
                </div>
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="fullmailbox"
                    checked={pending.accountFullMailbox}
                    onCheckedChange={(v) => setPending({ ...pending, accountFullMailbox: !!v })}
                  />
                  <label htmlFor="fullmailbox" className="text-sm">Full Mailbox</label>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-2 pt-2 border-t">
              <Button size="sm" onClick={handleApply} className="flex-1">
                Apply
              </Button>
              <Button size="sm" variant="outline" onClick={handleReset}>
                <RotateCcw className="h-3.5 w-3.5 mr-1" />
                Reset
              </Button>
            </div>
          </div>
        </PopoverContent>
      </Popover>

      {activeFilters.map((f) => (
        <Badge
          key={f.key}
          variant="secondary"
          className="flex items-center gap-1 pr-1 cursor-pointer hover:bg-muted"
          onClick={() => removeFilter(f.key, f.value)}
        >
          {f.label}
          <X className="h-3 w-3" />
        </Badge>
      ))}
    </div>
  );
}
